/**
 * Configurações da aplicação baseadas em variáveis de ambiente
 *
 * No Vite, as variáveis de ambiente devem começar com VITE_ para serem expostas no cliente
 * Os arquivos são carregados automaticamente na seguinte ordem:
 * 1. .env
 * 2. .env.local
 * 3. .env.[mode]
 * 4. .env.[mode].local
 */

interface AppConfig {
  api: {
    baseUrl: string;
  };
  google: {
    clientId: string | undefined;
  };
  sentry: {
    dsn: string;
    environment: string;
    release: string;
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
  };
  app: {
    env: string;
    title: string;
    isDevelopment: boolean;
    isProduction: boolean;
    isStaging: boolean;
    enableDevtools: boolean;
  };
}

function validateConfig(): void {
  const requiredVariables = ['VITE_API_BASE_URL'];

  const missingVariables = requiredVariables.filter((name) => !import.meta.env[name]);

  if (missingVariables.length > 0) {
    throw new Error(`Missing required environment variables: ${missingVariables.join(', ')}`);
  }
}

function getEnvironment(): string {
  return import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development';
}

function toNumber(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function createConfig(): AppConfig {
  validateConfig();

  const environment = getEnvironment();

  const config: AppConfig = {
    api: {
      baseUrl: import.meta.env.VITE_API_BASE_URL,
    },
    google: {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    },
    sentry: {
      dsn: import.meta.env.VITE_SENTRY_DSN || '',
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || environment,
      release: import.meta.env.VITE_SENTRY_RELEASE || '',
      tracesSampleRate: toNumber(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
        0.02,
      ),
      replaysSessionSampleRate: toNumber(
        import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
        0.05,
      ),
      replaysOnErrorSampleRate: toNumber(
        import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
        1,
      ),
    },
    app: {
      env: environment,
      title: import.meta.env.VITE_APP_TITLE || 'Asseto',
      isDevelopment: environment === 'development',
      isProduction: environment === 'production',
      isStaging: environment === 'staging',
      enableDevtools:
        import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' ||
        environment === 'development' ||
        environment === 'staging',
    },
  };

  // Log configuration in development
  if (config.app.isDevelopment || config.app.isStaging) {
    console.log('🔧 App Configuration', {
      Environment: config.app.env,
      'API Base URL': config.api.baseUrl,
      'Sentry Enabled': Boolean(config.sentry.dsn),
      'Sentry Release': config.sentry.release || 'local',
      'Dev Mode': config.app.isDevelopment,
      'Devtools Enabled': config.app.enableDevtools,
    });
  }

  return config;
}

export const config = createConfig();

// Função helper para debug
export const logConfig = (): void => {
  if (config.app.enableDevtools) {
    console.group('🔧 App Configuration');
    console.log('Environment:', config.app.env);
    console.log('API Base URL:', config.api.baseUrl);
    console.log('Sentry Enabled:', Boolean(config.sentry.dsn));
    console.log('Sentry Release:', config.sentry.release || 'local');
    console.log('Dev Mode:', config.app.isDevelopment);
    console.log('Devtools Enabled:', config.app.enableDevtools);
    console.groupEnd();
  }
};

// Executar validação na inicialização
if (config.app.isDevelopment) {
  validateConfig();
  logConfig();
}
